'use client'

import { useEffect, useMemo } from 'react'
import { usePathname } from 'next/navigation'

import { SearchModal } from '@common'
import { Sidebar } from '@common/components/Sidebar'

import { useLayoutStore } from '@stores/layout/layout.store'

import { useAcademySearch } from '@/shared/components/AcademySearchModal'
import { DocsMobileMenu } from '@/shared/components/DocsMobileMenu'
import { DOCS_ROUTES, SIDEBAR_MENU_KEYS } from '@/shared/docs-shared.const'
import { isDocumentationPath } from '@/shared/docs-shared.utils'
import { ACADEMY_LAYOUT_TEST_IDS } from '@/shared/docs-test-ids.const'
import { getAcademyBaseUrl } from '@/shared/utils/docsSidebarNav'

import { DocsHeaderWrapper } from '../DocsHeaderWrapper'
import { DocumentationMenu } from '../DocumentationMenu'
import { ScrollToTopButton } from '../ScrollToTopButton'
import { TutorialMenu } from '../TutorialMenu'

import { useIsMobile, useMobileMenu, useNavigationIntercept } from './hooks'
import type { AcademyLayoutProps } from './types'
import { buildMenuItems, getLayoutClassName } from './utils'

export function DocsLayout({
  children,
  sidebarChildren,
  tutorialSections,
  currentTutorialPath,
  breadcrumbs,
  categoryTree,
  rootArticles,
}: AcademyLayoutProps) {
  const { sidebarCollapsed } = useLayoutStore()
  const pathname = usePathname()
  const { openModal: openDocumentationSearch, searchModalProps: documentationSearchModalProps } = useAcademySearch()

  useNavigationIntercept()

  const isMobile = useIsMobile()
  const { isMobileMenuOpen, handleToggle, handleClose } = useMobileMenu(pathname, isMobile)

  const isQuickStartPage = pathname.startsWith(DOCS_ROUTES.quickStart)
  const isDocumentationPage = isDocumentationPath(pathname ?? '')

  const menuItems = useMemo(() => buildMenuItems(pathname ?? ''), [pathname])
  const layoutClassName = getLayoutClassName(sidebarCollapsed, false)

  useEffect(() => {
    const handleGlobalSearchShortcut = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'k') {
        return
      }
      if (event.defaultPrevented) {
        return
      }
      if (documentationSearchModalProps.open) {
        return
      }
      const target = event.target as HTMLElement | null
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return
      }
      event.preventDefault()
      openDocumentationSearch()
    }

    window.addEventListener('keydown', handleGlobalSearchShortcut)
    return () => window.removeEventListener('keydown', handleGlobalSearchShortcut)
  }, [documentationSearchModalProps.open, openDocumentationSearch])

  const finalSidebarChildren = useMemo(() => {
    if (isQuickStartPage && tutorialSections?.length) {
      return (
        <>
          {sidebarChildren}
          <TutorialMenu
            sections={tutorialSections}
            currentPath={currentTutorialPath}
            basePath={DOCS_ROUTES.quickStart}
            showCompletion={false}
          />
        </>
      )
    }

    if (isDocumentationPage && tutorialSections?.length) {
      return (
        <>
          {sidebarChildren}
          <DocumentationMenu
            sections={tutorialSections}
            currentPath={currentTutorialPath}
            categoryTree={categoryTree}
            rootArticles={rootArticles}
            basePath=""
          />
        </>
      )
    }

    return sidebarChildren
  }, [
    isQuickStartPage,
    isDocumentationPage,
    tutorialSections,
    currentTutorialPath,
    sidebarChildren,
    categoryTree,
    rootArticles,
  ])

  const sidebar = useMemo(() => {
    const menuKey = isQuickStartPage && tutorialSections?.length ? SIDEBAR_MENU_KEYS.tutorial : SIDEBAR_MENU_KEYS.normal

    return (
      <Sidebar
        key={menuKey}
        selectedNavId="academy"
        logoHref={getAcademyBaseUrl()}
        menuGroups={menuItems}
        navbarTopSlot={null}
        showInviteButton={false}
        showWorkspaceSelector={false}
      >
        {finalSidebarChildren}
      </Sidebar>
    )
  }, [menuItems, finalSidebarChildren, isQuickStartPage, tutorialSections])

  return (
    <div data-testid={ACADEMY_LAYOUT_TEST_IDS.root} className={layoutClassName}>
      <aside className="sidebar">{sidebar}</aside>
      <div className="main-content">
        <header className="topbar">
          <DocsHeaderWrapper
            breadcrumbs={breadcrumbs}
            onMobileMenuToggle={handleToggle}
            isMobile={isMobile}
            documentationSearchEnabled
            onDocumentationSearchOpen={openDocumentationSearch}
          />
        </header>
        <main className="content">{children}</main>
      </div>
      <DocsMobileMenu
        isOpen={isMobileMenuOpen}
        onClose={handleClose}
        tutorialSections={tutorialSections}
        currentTutorialPath={currentTutorialPath}
        categoryTree={categoryTree}
        rootArticles={rootArticles}
      />
      <ScrollToTopButton />
      <SearchModal {...documentationSearchModalProps} />
    </div>
  )
}

export { DocsLayout as AcademyLayout }
